/**
 * Venda Canopus pelo api-server (POST /canopus/venda → 202 + jobId → GET /job/:id).
 * Mesmo esquema do sellServopa (api-client.ts): o proxy só devolve o andamento
 * ao servidor certo no formato /job/<id>, e o job só é lido por quem vendeu.
 */
import { getApiBaseUrl } from './api-client';

export type SimNao = boolean;

export interface ClienteCanopus {
  nome: string;
  cpf: string;
  nascimento: string; // DD/MM/AAAA
  sexo: 'M' | 'F';
  rg: string;
  rg_emissao: string; // DD/MM/AAAA
  rg_orgao: string;
  estado_civil: string; // texto do AFV: SOLTEIRO, CASADO, ...
  profissao: string; // nome exato do AFV
  email: string;
  celular: string;
  telefone: string;
  cep: string;
  numero: string;
  complemento?: string;
  cep_comercial?: string;
  numero_comercial?: string;
  complemento_comercial?: string;
  nacionalidade: string;
  uf_nascimento: string;
  cidade_nascimento: string;
  renda: string;
  nome_mae: string;
  nome_pai?: string;
  pessoa_politicamente_exposta: SimNao;
  ocupou_cargo_publico: SimNao;
  parente_cargo_publico: SimNao;
  conta_bancaria: 'nao_desejo_informar' | 'nao_possuo';
  documentos_pf: string[];
  conjuge?: { nome: string; cpf: string; rg: string; nascimento: string; rg_orgao: string; profissao: string; sexo: 'M' | 'F' };
}

export interface VendaCanopusPedido {
  cliente: ClienteCanopus;
  plano: { codigo: string; valor: number; prazo: number; tipo?: string; produto?: number };
  pedido: { forma_contato: number; comprovante_renda: number; informacoes?: string };
  /** true = envia à Canopus (o cliente recebe contrato e boleto). */
  finalizar: boolean;
  /** Contato do CRM — vai para o registro da venda (canopus_vendas_app). */
  contato_id?: number | string;
}

export interface VendaCanopusResultado {
  administradora: 'canopus';
  id_cliente: string;
  id_pedido: string;
  grupo: string;
  prazo: number;
  primeira_parcela: number;
  taxa_adm: number;
  finalizado: boolean;
  pendencia: string | null;
  /** false = a venda foi feita, mas o registro na North falhou. */
  registrada?: boolean;
}

export interface EnvioCanopusResultado {
  administradora: 'canopus';
  id_pedido: string;
  finalizado: boolean;
  pendencia: string | null;
  registrada?: boolean;
}

const DESFECHO_DESCONHECIDO =
  'Conexão perdida durante a venda Canopus. A venda PODE ter sido registrada; confira na Canopus antes de refazer.';

async function lerJson(r: Response): Promise<any> {
  try {
    const t = await r.text();
    return t ? JSON.parse(t) : {};
  } catch {
    return {};
  }
}

/** Sugestões de profissão (a Canopus só aceita o nome exato da lista dela). */
export async function buscarProfissoesCanopus(termo: string, accessToken: string): Promise<string[]> {
  if (termo.trim().length < 3) return [];
  const r = await fetch(`${getApiBaseUrl()}/canopus/profissoes?termo=${encodeURIComponent(termo.trim())}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const j = await lerJson(r);
  return Array.isArray(j.profissoes) ? j.profissoes : [];
}

/** Acompanha o job do servidor até terminar (2 s entre consultas, até 5 min). */
async function acompanharJob<T>(
  base: string,
  jobId: string,
  auth: Record<string, string>,
  aoAndar: (mensagem: string) => void,
): Promise<T> {
  const limite = Date.now() + 5 * 60 * 1000;
  let falhasDeRede = 0;
  while (Date.now() < limite) {
    await new Promise((ok) => setTimeout(ok, 2000));
    let resp: Response;
    try {
      resp = await fetch(`${base}/job/${jobId}`, { headers: auth });
    } catch {
      if (++falhasDeRede >= 5) throw new Error(DESFECHO_DESCONHECIDO);
      continue;
    }
    falhasDeRede = 0;
    if (resp.status === 404) throw new Error(DESFECHO_DESCONHECIDO);
    const job = await lerJson(resp);
    if (job.status === 'completed') return job.result as T;
    if (job.status === 'error') {
      const msg = String(job.message || '');
      const pedido = job.id_pedido && !msg.includes(String(job.id_pedido)) ? ` (o pedido ${job.id_pedido} ficou como rascunho na Canopus)` : '';
      throw new Error(`${msg || 'A venda Canopus falhou.'}${pedido}`);
    }
    if (job.message) aoAndar(job.message);
  }
  throw new Error(DESFECHO_DESCONHECIDO);
}

/**
 * Vende e acompanha até o fim. `aoAndar` recebe as mensagens do servidor
 * ("Cadastrando o cliente…", "Criando o pedido…").
 */
export async function venderCanopus(
  venda: VendaCanopusPedido,
  accessToken: string,
  aoAndar: (mensagem: string) => void = () => {},
): Promise<VendaCanopusResultado> {
  const base = getApiBaseUrl();
  const auth = { Authorization: `Bearer ${accessToken}` };
  let r: Response;
  try {
    r = await fetch(`${base}/canopus/venda`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...auth },
      body: JSON.stringify(venda),
    });
  } catch {
    throw new Error('Sem conexão com o servidor. Nada foi enviado à Canopus.');
  }
  const j = await lerJson(r);
  if (r.status !== 202 || !j.jobId) {
    throw new Error(j.message || `O servidor recusou a venda (HTTP ${r.status}).`);
  }

  return acompanharJob<VendaCanopusResultado>(base, j.jobId, auth, aoAndar);
}

/**
 * Envia à Canopus um pedido que ficou salvo como rascunho. É a venda real: o
 * cliente recebe contrato e boleto. O servidor exige `confirmar: true`.
 */
export async function enviarVendaCanopus(
  idPedido: string | number,
  accessToken: string,
  aoAndar: (mensagem: string) => void = () => {},
): Promise<EnvioCanopusResultado> {
  const base = getApiBaseUrl();
  const auth = { Authorization: `Bearer ${accessToken}` };
  let r: Response;
  try {
    r = await fetch(`${base}/canopus/venda/${encodeURIComponent(String(idPedido))}/enviar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...auth },
      body: JSON.stringify({ confirmar: true }),
    });
  } catch {
    throw new Error('Sem conexão com o servidor. Nada foi enviado à Canopus.');
  }
  const j = await lerJson(r);
  if (r.status !== 202 || !j.jobId) {
    throw new Error(j.message || `O servidor recusou o envio (HTTP ${r.status}).`);
  }
  return acompanharJob<EnvioCanopusResultado>(base, j.jobId, auth, aoAndar);
}

export type DocumentoCanopus = 'boleto' | 'proposta' | 'atestado';

/** Baixa boleto/proposta/atestado do contrato (o servidor busca com a sessão do robô). */
export async function baixarDocumentoCanopus(idPedido: string | number, tipo: DocumentoCanopus, accessToken: string): Promise<Blob> {
  let r: Response;
  try {
    r = await fetch(`${getApiBaseUrl()}/canopus/venda/${encodeURIComponent(String(idPedido))}/documento/${tipo}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch {
    throw new Error('Sem conexão com o servidor.');
  }
  if (!r.ok) {
    const j = await lerJson(r);
    throw new Error(j.message || `Não foi possível baixar o ${tipo} (HTTP ${r.status}).`);
  }
  return r.blob();
}
